import { useState } from 'react';
import { toast } from 'sonner';
import { JobCard } from './JobCard';
import { useAppStore } from '../store/appStore';
import { computeJobFinancials } from '../lib/jobFinancials';

const COLUMNS = [
  { id: 'draft', title: 'Drafts', statuses: ['draft'] },
  { id: 'sent', title: 'Sent', statuses: ['sent', 'quoted'] },
  { id: 'booked', title: 'Booked', statuses: ['booked'] },
];

const KNOWN_STATUSES = COLUMNS.flatMap((c) => c.statuses);

/**
 * Trello-style Kanban board for active jobs.
 * Jobs can be dragged between Drafts / Sent / Booked columns, which updates
 * the job's status via the existing PUT /jobs/{id} endpoint.
 *
 * Props:
 *   jobs           — array of active job objects (already filtered, no completed/lost)
 *   formatCurrency — currency formatter from useRegion()
 */
export function JobKanban({ jobs, formatCurrency, briefsById = {}, treatmentsByJob = {} }) {
  const { updateJob } = useAppStore();
  const [draggingId, setDraggingId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  const handleDragStart = (e, jobId) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', jobId);

    // Clone the card off-screen for use as the drag image. Using a clone
    // avoids issues where `hover:scale-[1.02]` / shadows / blurred orbs on the
    // live element make the native drag snapshot appear blank or distorted.
    const node = e.currentTarget;
    const rect = node.getBoundingClientRect();
    try {
      const clone = node.cloneNode(true);
      clone.style.position = 'fixed';
      clone.style.top = '-10000px';
      clone.style.left = '-10000px';
      clone.style.width = `${rect.width}px`;
      clone.style.height = `${rect.height}px`;
      clone.style.transform = 'none';
      clone.style.opacity = '1';
      clone.style.pointerEvents = 'none';
      // Also strip transforms from any nested transformed children (scale on hover, etc.)
      clone.querySelectorAll('*').forEach((el) => {
        el.style.transform = 'none';
        el.style.transition = 'none';
      });
      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(
        clone,
        e.clientX - rect.left,
        e.clientY - rect.top
      );
      // Remove the clone after the browser has captured the drag image
      setTimeout(() => {
        if (clone.parentNode) clone.parentNode.removeChild(clone);
      }, 0);
    } catch (_) {
      // setDragImage unsupported — browser falls back to its own snapshot.
    }

    // Defer source-dimming so the captured drag image isn't dimmed.
    requestAnimationFrame(() => setDraggingId(jobId));
  };

  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumn !== columnId) {
      setDragOverColumn(columnId);
    }
  };

  const handleDragLeave = (e, columnId) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      if (dragOverColumn === columnId) {
        setDragOverColumn(null);
      }
    }
  };

  const handleDrop = async (e, columnId) => {
    e.preventDefault();
    const jobId = e.dataTransfer.getData('text/plain');
    setDraggingId(null);
    setDragOverColumn(null);

    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;

    const targetCol = COLUMNS.find((c) => c.id === columnId);
    if (!targetCol || targetCol.statuses.includes(job.status)) {
      return;
    }

    try {
      await updateJob(jobId, { status: columnId });
      toast.success(`Moved to ${targetCol.title}`);
    } catch (err) {
      console.error('Failed to update job status:', err);
      toast.error('Failed to update job status');
    }
  };

  // Group jobs by column. Unknown active statuses fall into Drafts.
  const grouped = COLUMNS.map((col) => {
    let colJobs = jobs.filter((j) => col.statuses.includes(j.status));
    if (col.id === 'draft') {
      const unknown = jobs.filter((j) => !KNOWN_STATUSES.includes(j.status));
      colJobs = [...colJobs, ...unknown];
    }
    const profit = colJobs.reduce((s, j) => s + computeJobFinancials(j).profit, 0);
    const total = colJobs.reduce((s, j) => s + (Number(j.totals?.grand) || 0), 0);
    return { ...col, jobs: colJobs, profit, total };
  });

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start"
      data-testid="job-kanban-board"
    >
      {grouped.map((col) => {
        const isOver = dragOverColumn === col.id;
        return (
          <section
            key={col.id}
            aria-label={col.title}
            onDragOver={(e) => handleDragOver(e, col.id)}
            onDragLeave={(e) => handleDragLeave(e, col.id)}
            onDrop={(e) => handleDrop(e, col.id)}
            className={`flex flex-col gap-6 rounded-tile p-4 pb-6 min-w-0 transition-colors ${
              isOver ? 'bg-mist' : 'bg-surface'
            }`}
            data-testid={`kanban-column-${col.id}`}
          >
            {/* Column head: lowercase name and count, then its value and profit */}
            <div className="flex flex-col gap-2 px-1 pt-1">
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-[22px] font-normal lowercase" data-testid={`kanban-column-${col.id}-pill`}>
                  {col.title}
                </h2>
                <span className="text-sm text-muted-foreground">{col.jobs.length}</span>
              </div>
              <div className="flex justify-between gap-2 text-sm">
                <span>{formatCurrency(col.total)}</span>
                <span
                  className={col.profit >= 0 ? 'text-muted-foreground' : 'text-brick'}
                  data-testid={`kanban-column-${col.id}-profit`}
                >
                  profit {formatCurrency(col.profit)}
                </span>
              </div>
            </div>

            {/* Cards */}
            <div className="flex flex-col gap-4 min-h-[120px]">
              {col.jobs.length === 0 ? (
                <div className="flex items-center justify-center h-24 border border-dashed border-line-strong/60 rounded-cards text-sm text-muted-foreground italic">
                  Drop jobs here
                </div>
              ) : (
                col.jobs.map((job) => (
                  <div
                    key={job.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, job.id)}
                    onDragEnd={handleDragEnd}
                    className={`cursor-grab active:cursor-grabbing transition-opacity ${
                      draggingId === job.id ? 'opacity-40' : 'opacity-100'
                    }`}
                    data-testid={`kanban-card-${job.id}`}
                  >
                    <JobCard
                      job={job}
                      formatCurrency={formatCurrency}
                      brief={job.brief_id ? briefsById[job.brief_id] : null}
                      treatments={treatmentsByJob[job.id] || []}
                    />
                  </div>
                ))
              )}
            </div>
          </section>
        );
      })}
    </div>
  );
}
